import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  ReceiptIcon, MoneyIcon, PlusIcon, TrashIcon, ProhibitIcon, EyeIcon, ChartPieSliceIcon, CalculatorIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function Forms() {
  const [tab, setTab] = useState("ventas");
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState({ categorias: [], partidas: [], proveedores: [], cuentas_banco: [] });

  useEffect(() => {
    api.get("/forms/summary").then(({ data }) => setSummary(data));
    api.get("/forms/categorias").then(({ data }) => setCatalog(data));
  }, []);

  const refresh = () => api.get("/forms/summary").then(({ data }) => setSummary(data));

  return (
    <Layout title="Formularios" subtitle="Ventas y gastos al contado (sin crédito) — registro directo con asiento automático">
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Kpi label="Ventas HOY" v={fmtL(summary.ventas_hoy.total)} sub={`${summary.ventas_hoy.count} ops`} c="green" />
          <Kpi label="Ventas del mes" v={fmtL(summary.ventas_mes.total)} sub={`${summary.ventas_mes.count} ops`} c="green" />
          <Kpi label="Gastos HOY" v={fmtL(summary.gastos_hoy.total)} sub={`${summary.gastos_hoy.count} ops`} c="red" />
          <Kpi label="Neto del mes" v={fmtL(summary.neto_mes)} sub={summary.neto_mes >= 0 ? "favorable" : "deficitario"} c={summary.neto_mes >= 0 ? "green" : "red"} />
        </div>
      )}

      <div className="flex gap-1 mb-5 border-b border-border/60" data-testid="forms-tabs">
        <T active={tab === "ventas"} onClick={() => setTab("ventas")} icon={ReceiptIcon} testid="tab-ventas">Ventas al contado</T>
        <T active={tab === "gastos"} onClick={() => setTab("gastos")} icon={MoneyIcon} testid="tab-gastos">Gastos al contado</T>
      </div>

      {tab === "ventas" && <CashSalesTab catalog={catalog} onChange={refresh} />}
      {tab === "gastos" && <CashExpensesTab catalog={catalog} onChange={refresh} />}
    </Layout>
  );
}

function T({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

function Kpi({ label, v, sub, c }) {
  const cls = { green: "text-agro-green", red: "text-red-400", yellow: "text-agro-yellow" }[c];
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl mt-0.5 ${cls}`}>{v}</div>
      <div className="text-[10px] text-text-muted mt-0.5">{sub}</div>
    </div>
  );
}

/* ===================== VENTAS AL CONTADO ===================== */
function CashSalesTab({ catalog, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/forms/cash-sales"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onVoid = async (s) => {
    if (!window.confirm(`Anular venta ${s.numero}? Se revierte el asiento, tesorería e inventario.`)) return;
    try { await api.post(`/forms/cash-sales/${s.id}/void`); toast.success("Anulada"); load(); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xs text-text-secondary">{items.length} ventas al contado</div>
        <button onClick={() => setCreating(true)} data-testid="new-cash-sale" className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" /> Nueva venta al contado
        </button>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Número</th><th className="p-3">Cliente</th>
              <th className="p-3">Método</th><th className="p-3">Cuenta</th>
              <th className="p-3 text-right"># Ítems</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3">Fecha</th><th className="p-3 pr-5 text-right">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="p-6 text-center text-text-muted">Cargando…</td></tr>
            : items.length === 0 ? <tr><td colSpan="8" className="p-10 text-center text-text-muted">Sin ventas registradas</td></tr>
            : items.map((s) => (
              <tr key={s.id} className={`border-t border-border/30 hover:bg-white/[0.02] ${s.estado === "anulada" ? "opacity-50" : ""}`}>
                <td className="p-3 pl-5 font-mono text-agro-green">{s.numero}</td>
                <td className="p-3">{s.cliente_nombre}<div className="text-[10px] text-text-muted">{s.cliente_rtn || "—"}</div></td>
                <td className="p-3 uppercase text-[10px]">{s.metodo}</td>
                <td className="p-3 text-text-secondary">{s.cuenta_banco_codigo} {s.cuenta_banco_nombre}</td>
                <td className="p-3 text-right">{s.items?.length}</td>
                <td className="p-3 text-right font-medium">
                  {fmtL(s.total)}
                  {s.costo_total != null && (
                    <div className="text-[10px] text-text-muted" title="Costo de venta PEPS">
                      costo PEPS {fmtL(s.costo_total)}{(s.faltantes_peps || []).length > 0 && <span className="text-agro-yellow"> · sin lote suficiente</span>}
                    </div>
                  )}
                </td>
                <td className="p-3 text-text-secondary">{fmtDateTime(s.fecha)}</td>
                <td className="p-3 pr-5 text-right">
                  {s.estado === "registrada" && (
                    <button onClick={() => onVoid(s)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" title="Anular" aria-label="Anular"><ProhibitIcon size={14} /></button>
                  )}
                  {s.estado === "anulada" && <span className="text-[10px] uppercase text-red-400">anulada</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewCashSaleModal catalog={catalog} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); onChange(); }} />}
    </div>
  );
}

function NewCashSaleModal({ catalog, onClose, onCreated }) {
  const [f, setF] = useState({
    cliente_id: "", cliente_nombre: "Consumidor final", cliente_rtn: "",
    metodo: "efectivo", cuenta_banco_id: "", referencia: "", descuento: 0,
    items: [{ codigo: "", descripcion: "", cantidad: 1, precio_unitario: 0, isv_pct: 15 }],
  });
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data || []));
    api.get("/clients").then(({ data }) => setClients(data || []));
  }, []);

  const addItem = () => setF({ ...f, items: [...f.items, { codigo: "", descripcion: "", cantidad: 1, precio_unitario: 0, isv_pct: 15 }] });
  const removeItem = (i) => setF({ ...f, items: f.items.filter((_, j) => j !== i) });
  const updateItem = (i, k, v) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "codigo") {
      const p = products.find((p) => p.codigo === v);
      if (p) {
        items[i].descripcion = p.descripcion;
        items[i].precio_unitario = p.precio_venta || p.precio_p1 || 0;
        items[i].producto_id = p.id;
      }
    }
    setF({ ...f, items });
  };

  const totals = useMemo(() => {
    let subG = 0, subE = 0, isv = 0;
    f.items.forEach((it) => {
      const subt = (it.cantidad || 0) * (it.precio_unitario || 0);
      if (it.isv_pct > 0) { subG += subt; isv += subt * (it.isv_pct / 100); }
      else subE += subt;
    });
    const total = subG + subE + isv - (f.descuento || 0);
    return { subtotal: subG + subE, isv, total };
  }, [f.items, f.descuento]);

  const save = async () => {
    if (f.items.some((it) => !it.descripcion || !it.cantidad || !it.precio_unitario)) return toast.error("Completa todos los ítems");
    setSaving(true);
    try {
      await api.post("/forms/cash-sales", f);
      toast.success("Venta al contado registrada con asiento automático");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const m = useModalForm({ value: f, onClose, onSave: save });
  return (
    <Modal title="Nueva venta al contado" wide m={m}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Cliente">
            <select value={f.cliente_id} onChange={(e) => {
              const c = clients.find((c) => c.id === e.target.value);
              setF({ ...f, cliente_id: e.target.value, cliente_nombre: c?.nombre || "Consumidor final", cliente_rtn: c?.rtn || "" });
            }} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
              <option value="">Consumidor final</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Field>
          <Field label="RTN cliente">
            <input value={f.cliente_rtn} onChange={(e) => setF({ ...f, cliente_rtn: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" placeholder="opcional" />
          </Field>
          <Field label="Método de pago">
            <select value={f.metodo} onChange={(e) => setF({ ...f, metodo: e.target.value, cuenta_banco_id: "" })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
              <option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option><option value="tarjeta">Tarjeta</option><option value="cheque">Cheque</option><option value="deposito">Depósito</option>
            </select>
          </Field>
          <Field label="Cuenta destino">
            <select value={f.cuenta_banco_id} onChange={(e) => setF({ ...f, cuenta_banco_id: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
              <option value="">Auto por método</option>
              {catalog.cuentas_banco.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.nombre} ({c.tipo})</option>)}
            </select>
          </Field>
          <Field label="Referencia">
            <input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" placeholder="N° de comprobante" />
          </Field>
          <Field label="Descuento (L.)">
            <input type="number" step="0.01" value={f.descuento} onChange={(e) => setF({ ...f, descuento: parseFloat(e.target.value) || 0 })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>

        {/* Items */}
        <div className="border border-border rounded-md overflow-hidden">
          <div className="bg-sidebar p-2 flex justify-between items-center">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Ítems</span>
            <button onClick={addItem} className="text-xs flex items-center gap-1 text-agro-green hover:underline"><PlusIcon size={10} /> Agregar</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-app/40">
              <tr className="text-left text-[10px] uppercase text-text-muted">
                <th className="p-2 pl-3 w-32">Código</th><th className="p-2">Descripción</th>
                <th className="p-2 text-right w-20">Cant.</th><th className="p-2 text-right w-28">Precio</th>
                <th className="p-2 text-right w-20">ISV%</th><th className="p-2 text-right w-28">Total</th>
                <th className="p-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {f.items.map((it, i) => {
                const subt = (it.cantidad || 0) * (it.precio_unitario || 0);
                const totalLn = subt + subt * (it.isv_pct / 100);
                return (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-1 pl-3">
                      <input list="prods" value={it.codigo} onChange={(e) => updateItem(i, "codigo", e.target.value)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs font-mono" />
                    </td>
                    <td className="p-1">
                      <input value={it.descripcion} onChange={(e) => updateItem(i, "descripcion", e.target.value)} className="w-full bg-app border border-border rounded px-2 py-1 text-xs" />
                    </td>
                    <td className="p-1">
                      <input type="number" step="0.01" value={it.cantidad} onChange={(e) => updateItem(i, "cantidad", parseFloat(e.target.value) || 0)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs text-right" />
                    </td>
                    <td className="p-1">
                      <input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => updateItem(i, "precio_unitario", parseFloat(e.target.value) || 0)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs text-right" />
                    </td>
                    <td className="p-1">
                      <select value={it.isv_pct} onChange={(e) => updateItem(i, "isv_pct", parseFloat(e.target.value))} className="w-full bg-app border border-border rounded px-1 py-1 text-xs">
                        <option value="0">Ex</option><option value="15">15</option><option value="18">18</option>
                      </select>
                    </td>
                    <td className="p-1 text-right font-medium">{fmtL(totalLn)}</td>
                    <td className="p-1">{f.items.length > 1 && <button aria-label="Eliminar" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300"><TrashIcon size={12} /></button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="prods">
            {products.map((p) => <option key={p.id} value={p.codigo}>{p.descripcion} - L {p.precio_venta || p.precio_p1 || 0}</option>)}
          </datalist>
        </div>

        {/* Totales */}
        <div className="grid grid-cols-3 gap-3 bg-app/40 border border-border rounded p-3 text-sm">
          <div><span className="text-text-muted text-xs">Subtotal:</span><div className="font-medium">{fmtL(totals.subtotal)}</div></div>
          <div><span className="text-text-muted text-xs">ISV:</span><div className="font-medium">{fmtL(totals.isv)}</div></div>
          <div><span className="text-text-muted text-xs">Total:</span><div className="font-heading text-xl text-agro-green">{fmtL(totals.total)}</div></div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
          <button onClick={m.guardar} disabled={saving || m.saving} data-testid="save-cash-sale" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{saving || m.saving ? "Guardando…" : "Registrar venta"}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ===================== GASTOS AL CONTADO ===================== */
function CashExpensesTab({ catalog, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/forms/cash-expenses"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xs text-text-secondary">{items.length} gastos al contado</div>
        <button onClick={() => setCreating(true)} data-testid="new-cash-expense" className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" /> Nuevo gasto al contado
        </button>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Número</th><th className="p-3">Concepto</th>
              <th className="p-3">Método</th><th className="p-3">Distrib.</th>
              <th className="p-3 text-right">Monto</th>
              <th className="p-3">Fecha</th><th className="p-3 pr-5 text-right">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="p-6 text-center text-text-muted">Cargando…</td></tr>
            : items.length === 0 ? <tr><td colSpan="7" className="p-10 text-center text-text-muted">Sin gastos registrados</td></tr>
            : items.map((g) => (
              <tr key={g.id} className="border-t border-border/30 hover:bg-white/[0.02]">
                <td className="p-3 pl-5 font-mono text-agro-green">{g.numero}</td>
                <td className="p-3">{g.concepto}<div className="text-[10px] text-text-muted">{g.referencia || "—"}</div></td>
                <td className="p-3 uppercase text-[10px]">{g.metodo}</td>
                <td className="p-3 text-text-secondary">{g.lineas?.length} línea{g.lineas?.length !== 1 && "s"}</td>
                <td className="p-3 text-right font-medium text-red-400">{fmtL(g.monto_total)}</td>
                <td className="p-3 text-text-secondary">{fmtDateTime(g.fecha)}</td>
                <td className="p-3 pr-5 text-right">
                  <button onClick={() => setViewing(g)} className="p-1.5 text-text-muted hover:text-agro-green" title="Ver distribución" aria-label="Ver distribución"><ChartPieSliceIcon size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewCashExpenseModal catalog={catalog} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); onChange(); }} />}
      {viewing && <ExpenseDetailModal expense={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function NewCashExpenseModal({ catalog, onClose, onCreated }) {
  const [f, setF] = useState({
    concepto: "", referencia: "", monto_total: 0, metodo: "efectivo", cuenta_banco_id: "",
    modo: "porcentaje",
    lineas: [{ partida_codigo: "", proveedor_id: "", categoria: "", porcentaje: 100, monto: 0, descripcion: "" }],
  });
  const [saving, setSaving] = useState(false);

  const updateLine = (i, k, v) => {
    const lineas = [...f.lineas];
    lineas[i] = { ...lineas[i], [k]: v };
    if (k === "partida_codigo") {
      const p = catalog.partidas.find((x) => x.codigo === v);
      if (p) lineas[i].partida_nombre = p.nombre;
    }
    if (k === "proveedor_id") {
      const p = catalog.proveedores.find((x) => x.id === v);
      if (p) lineas[i].proveedor_nombre = p.nombre;
    }
    setF({ ...f, lineas });
  };
  const addLine = () => setF({ ...f, lineas: [...f.lineas, { partida_codigo: "", proveedor_id: "", categoria: "", porcentaje: 0, monto: 0, descripcion: "" }] });
  const removeLine = (i) => setF({ ...f, lineas: f.lineas.filter((_, j) => j !== i) });
  const distribuirEqual = () => {
    const n = f.lineas.length;
    if (!n) return;
    const each = +(100 / n).toFixed(2);
    const lineas = f.lineas.map((l, i) => ({ ...l, porcentaje: i === 0 ? +(100 - each * (n - 1)).toFixed(2) : each }));
    setF({ ...f, modo: "porcentaje", lineas });
  };

  const totalPct = useMemo(() => f.lineas.reduce((s, l) => s + (parseFloat(l.porcentaje) || 0), 0), [f.lineas]);
  const totalMontos = useMemo(() => f.lineas.reduce((s, l) => s + (parseFloat(l.monto) || 0), 0), [f.lineas]);

  const save = async () => {
    if (!f.concepto) return toast.error("Concepto obligatorio");
    if (!f.monto_total || f.monto_total <= 0) return toast.error("Monto total debe ser > 0");
    // Limpiar líneas según el modo
    const lineas = f.lineas.map((l) => ({
      partida_codigo: l.partida_codigo || "",
      partida_nombre: l.partida_nombre || "",
      proveedor_id: l.proveedor_id || null,
      proveedor_nombre: l.proveedor_nombre || "",
      categoria: l.categoria || "",
      porcentaje: f.modo === "porcentaje" ? (parseFloat(l.porcentaje) || 0) : 0,
      monto: f.modo === "monto" ? (parseFloat(l.monto) || 0) : 0,
      descripcion: l.descripcion || "",
    }));

    if (f.modo === "porcentaje" && Math.abs(totalPct - 100) > 0.01) return toast.error(`Los porcentajes deben sumar 100 (actual: ${totalPct.toFixed(2)})`);
    if (f.modo === "monto" && Math.abs(totalMontos - f.monto_total) > 0.01) return toast.error(`Suma de montos (${fmtL(totalMontos)}) debe igualar el total (${fmtL(f.monto_total)})`);

    setSaving(true);
    try {
      await api.post("/forms/cash-expenses", { ...f, lineas });
      toast.success("Gasto registrado con distribución contable");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const m = useModalForm({ value: f, onClose, onSave: save });
  return (
    <Modal title="Nuevo gasto al contado" wide m={m}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Concepto" span="md:col-span-2">
            <input value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" placeholder="Ej: Combustible camiones" />
          </Field>
          <Field label="Monto total (L.)">
            <input type="number" step="0.01" value={f.monto_total} onChange={(e) => setF({ ...f, monto_total: parseFloat(e.target.value) || 0 })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Referencia">
            <input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Método">
            <select value={f.metodo} onChange={(e) => setF({ ...f, metodo: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
              <option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option><option value="cheque">Cheque</option><option value="tarjeta">Tarjeta</option>
            </select>
          </Field>
          <Field label="Cuenta">
            <select value={f.cuenta_banco_id} onChange={(e) => setF({ ...f, cuenta_banco_id: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
              <option value="">Auto por método</option>
              {catalog.cuentas_banco.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.nombre}</option>)}
            </select>
          </Field>
          <Field label="Modo distribución" span="md:col-span-2">
            <div className="flex gap-2">
              <button onClick={() => setF({ ...f, modo: "porcentaje" })} className={`flex-1 text-xs px-3 py-1.5 rounded border ${f.modo === "porcentaje" ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted"}`}>% Porcentaje</button>
              <button onClick={() => setF({ ...f, modo: "monto" })} className={`flex-1 text-xs px-3 py-1.5 rounded border ${f.modo === "monto" ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted"}`}>L. Monto</button>
              <button onClick={distribuirEqual} className="text-xs px-3 py-1.5 rounded border border-border text-text-muted hover:text-text-primary flex items-center gap-1"><CalculatorIcon size={12} /> ÷</button>
            </div>
          </Field>
        </div>

        {/* Líneas de distribución */}
        <div className="border border-border rounded-md overflow-hidden">
          <div className="bg-sidebar p-2 flex justify-between items-center">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Distribución por categoría / partida / proveedor</span>
            <button onClick={addLine} className="text-xs flex items-center gap-1 text-agro-green"><PlusIcon size={10} /> Línea</button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-app/40">
              <tr className="text-left text-[10px] uppercase text-text-muted">
                <th className="p-2 pl-3">Partida (gasto)</th>
                <th className="p-2">Categoría</th>
                <th className="p-2">Proveedor</th>
                <th className="p-2 text-right w-20">{f.modo === "porcentaje" ? "%" : "Monto"}</th>
                <th className="p-2 text-right w-28">{f.modo === "porcentaje" ? "Monto calc." : "% calc."}</th>
                <th className="p-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {f.lineas.map((l, i) => {
                const montoCalc = f.modo === "porcentaje" ? (f.monto_total * (l.porcentaje || 0) / 100) : (l.monto || 0);
                const pctCalc = f.modo === "monto" && f.monto_total > 0 ? ((l.monto || 0) / f.monto_total * 100) : (l.porcentaje || 0);
                return (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-1 pl-3">
                      <select value={l.partida_codigo} onChange={(e) => updateLine(i, "partida_codigo", e.target.value)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs">
                        <option value="">— Selecciona —</option>
                        {catalog.partidas.map((p) => <option key={p.codigo} value={p.codigo}>{p.codigo} - {p.nombre}</option>)}
                      </select>
                    </td>
                    <td className="p-1">
                      <select value={l.categoria} onChange={(e) => updateLine(i, "categoria", e.target.value)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs">
                        <option value="">—</option>
                        {catalog.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="p-1">
                      <select value={l.proveedor_id} onChange={(e) => updateLine(i, "proveedor_id", e.target.value)} className="w-full bg-app border border-border rounded px-1 py-1 text-xs">
                        <option value="">—</option>
                        {catalog.proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </td>
                    <td className="p-1">
                      <input type="number" step="0.01" value={f.modo === "porcentaje" ? l.porcentaje : l.monto}
                        onChange={(e) => updateLine(i, f.modo === "porcentaje" ? "porcentaje" : "monto", parseFloat(e.target.value) || 0)}
                        className="w-full bg-app border border-border rounded px-1 py-1 text-xs text-right" />
                    </td>
                    <td className="p-1 text-right text-text-secondary">{f.modo === "porcentaje" ? fmtL(montoCalc) : `${pctCalc.toFixed(2)}%`}</td>
                    <td className="p-1">{f.lineas.length > 1 && <button aria-label="Eliminar" onClick={() => removeLine(i)} className="text-red-400"><TrashIcon size={12} /></button>}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-sidebar/40">
              <tr className="border-t-2 border-border">
                <td colSpan="3" className="p-2 pl-3 text-[10px] uppercase tracking-wider text-text-muted">Verificación</td>
                <td className={`p-2 text-right font-medium ${
                  (f.modo === "porcentaje" ? Math.abs(totalPct - 100) < 0.01 : Math.abs(totalMontos - f.monto_total) < 0.01)
                    ? "text-agro-green" : "text-red-400"
                }`}>{f.modo === "porcentaje" ? `${totalPct.toFixed(2)}%` : fmtL(totalMontos)}</td>
                <td className="p-2 text-right text-[10px] text-text-muted">{f.modo === "porcentaje" ? "debe = 100%" : `debe = ${fmtL(f.monto_total)}`}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={saving || m.saving} data-testid="save-cash-expense" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{saving || m.saving ? "Guardando…" : "Registrar gasto"}</button>
        </div>
      </div>
    </Modal>
  );
}

function ExpenseDetailModal({ expense, onClose }) {
  const m = useModalForm({ onClose });
  return (
    <Modal title={`Gasto ${expense.numero}`} m={m}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-text-muted">Concepto:</span> <strong>{expense.concepto}</strong></div>
          <div><span className="text-text-muted">Total:</span> <strong className="text-red-400">{fmtL(expense.monto_total)}</strong></div>
          <div><span className="text-text-muted">Método:</span> {expense.metodo}</div>
          <div><span className="text-text-muted">Cuenta:</span> {expense.cuenta_banco_codigo}</div>
        </div>
        <div className="border-t border-border pt-2">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Distribución</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-text-muted">
              <th className="p-1">Partida</th><th className="p-1">Categoría</th><th className="p-1">Proveedor</th><th className="p-1 text-right">%</th><th className="p-1 text-right">Monto</th>
            </tr></thead>
            <tbody>
              {expense.lineas.map((l, i) => (
                <tr key={i} className="border-t border-border/30">
                  <td className="p-1">{l.partida_codigo} {l.partida_nombre}</td>
                  <td className="p-1">{l.categoria || "—"}</td>
                  <td className="p-1">{l.proveedor_nombre || "—"}</td>
                  <td className="p-1 text-right">{(l.porcentaje || 0).toFixed(2)}%</td>
                  <td className="p-1 text-right font-medium">{fmtL(l.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, m, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className={`bg-card border border-border rounded-lg w-full ${wide ? "max-w-4xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`}>
        <div className="sticky top-0 bg-card border-b border-border p-4 flex justify-between items-center z-10">
          <h3 className="font-heading text-lg">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, span = "" }) {
  return (<div className={span}>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
    {children}
  </div>);
}
